import React, { useState } from 'react';
import { CompleteKundali, KundaliChartData, Language, PlanetName } from '../types/kundali';
import { generateComprehensiveRemedialPlan, ComprehensiveRemedialReport, PlanetaryRemedyDetail, DynamicTimingRemedy } from '../utils/remedialEngine';
import { PURUSHARTHA_SAFEGUARD } from '../utils/remediesBankEngine';
import { getPlanetFullName, getPlanetShortCode } from '../utils/language';
import {
  Sparkles,
  Shield,
  Activity,
  Gem,
  Heart,
  Flame,
  Zap,
  ChevronDown,
  ChevronUp,
  Info,
  Calendar,
  Clock,
  Compass,
  AlertTriangle,
  Award,
  CheckCircle2,
  Eye,
  EyeOff
} from 'lucide-react';
import { PopoverWrapper } from './PlanetaryHoverPopover';

interface UnifiedRemedialEngineViewProps {
  kundali: CompleteKundali;
  chartData: KundaliChartData;
  selectedHouseNumber?: number;
  onSelectHouse?: (houseNumber: number) => void;
  lang?: Language;
}

export const UnifiedRemedialEngineView: React.FC<UnifiedRemedialEngineViewProps> = ({
  kundali,
  chartData,
  selectedHouseNumber = 1,
  onSelectHouse,
  lang = 'en'
}) => {
  const isHi = lang === 'hi';
  const activeLang = lang;

  // View Mode: View 1 (Cumulative Natal Foundation) vs View 2 (Dynamic Current Gochar & Dasha Context)
  const [remedyViewMode, setRemedyViewMode] = useState<'cumulative' | 'dynamic'>('dynamic');
  const [expandedPlanets, setExpandedPlanets] = useState<Record<string, boolean>>({});
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('all');
  const [hoverInspectorEnabled, setHoverInspectorEnabled] = useState<boolean>(true);

  const report: ComprehensiveRemedialReport = generateComprehensiveRemedialPlan(kundali, chartData, selectedHouseNumber);

  const togglePlanet = (planet: string) => {
    setExpandedPlanets(prev => ({ ...prev, [planet]: !prev[planet] }));
  };

  const selectedHouseRemedy = report.houseRemedies[selectedHouseNumber];

  return (
    <div className="space-y-4">
      {/* Engine Navigation Ribbon */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-4 shadow-xs flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-xl bg-astro-primary/10 text-astro-primary flex items-center justify-center font-bold">
            <Flame className="w-4 h-4 text-astro-primary" />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm sm:text-base font-bold text-astro-primary font-serif">
                {isHi ? 'वैदिक उपाय एवं कर्मिक कार्ययोजना' : 'Upaya & Karmic Action Plan Engine'}
              </h3>
              <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-astro-teal-tint text-astro-primary border border-astro-primary/20">
                {remedyViewMode === 'dynamic' ? (isHi ? 'गतिशील / तात्कालिक' : 'Dynamic / Timing') : (isHi ? 'संचयी / जन्मजात' : 'Cumulative / Natal')}
              </span>
            </div>
            <p className="text-xs text-astro-secondary-text">
              {remedyViewMode === 'dynamic'
                ? (isHi ? 'सक्रिय दशा, साढ़ेसाती व गोचर आधारित तात्कालिक कर्मिक सुधार एवं व्यवहार' : 'Active Dasha, Sade Sati & Transit contextual karmic remedies & lifestyle alignment')
                : (isHi ? 'जन्मकालिक मूल प्रकृति (९ ग्रह) आधारित संचयी रत्न, जप, दान एवं शारीरिक उपाय' : 'Lifelong natal foundation remedies (9 Grahas) including Somatic, Mantra, Daan & Gemstones')}
            </p>
          </div>
        </div>

        {/* View Switcher Controls */}
        <div className="flex items-center gap-2">
          {/* Hover Inspector Toggle */}
          <button
            type="button"
            onClick={() => setHoverInspectorEnabled(!hoverInspectorEnabled)}
            className={`px-2.5 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 border transition-all cursor-pointer shadow-2xs ${
              hoverInspectorEnabled
                ? 'bg-astro-primary text-white border-astro-primary'
                : 'bg-astro-card text-astro-secondary-text border-astro-border hover:text-astro-primary'
            }`}
            title={isHi ? 'होवर पॉपओवर सक्षम/अक्षम करें' : 'Toggle rich hover tooltips'}
          >
            {hoverInspectorEnabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{hoverInspectorEnabled ? (isHi ? 'होवर: चालू' : 'Hover: ON') : (isHi ? 'होवर: बंद' : 'Hover: OFF')}</span>
          </button>

          {/* View 1 / View 2 Switch Buttons */}
          <div className="flex bg-astro-card p-1 rounded-xl border border-astro-border">
            <button
              onClick={() => setRemedyViewMode('dynamic')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                remedyViewMode === 'dynamic'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              <Zap className="w-3.5 h-3.5" />
              <span>{isHi ? 'गतिशील (दशा/गोचर)' : 'Dynamic (Dasha/Transit)'}</span>
            </button>
            <button
              onClick={() => setRemedyViewMode('cumulative')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                remedyViewMode === 'cumulative'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>{isHi ? 'संचयी (जन्मजात)' : 'Cumulative (Natal)'}</span>
            </button>
          </div>

          {/* Quick Jump to Dedicated Gemstone Advisor */}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('astro_navigate_tab', { detail: 'gemstones' }))}
            className="px-2.5 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 border border-astro-border bg-astro-card hover:bg-[#FAF6ED] text-astro-primary transition-all cursor-pointer shadow-2xs shrink-0"
            title={isHi ? 'रत्न परामर्श खोलें' : 'Open Gemstone Advisor'}
          >
            <Gem className="w-3.5 h-3.5 text-astro-accent" />
            <span className="hidden md:inline">{isHi ? 'रत्न परामर्श' : 'Gemstone Advisor'}</span>
          </button>
        </div>
      </div>

      {/* Universal Classical Purushartha Non-Guarantee Safeguard Banner */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 shadow-2xs flex items-start gap-3">
        <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <div className="space-y-1 text-xs">
          <h4 className="font-bold text-amber-900 dark:text-amber-200">
            {isHi ? PURUSHARTHA_SAFEGUARD.hi.title : PURUSHARTHA_SAFEGUARD.en.title}
          </h4>
          <p className="text-amber-800/90 dark:text-amber-300/90 leading-relaxed">
            {isHi ? PURUSHARTHA_SAFEGUARD.hi.text : PURUSHARTHA_SAFEGUARD.en.text}
          </p>
        </div>
      </div>

      {/* House-Context Active Ribbon for Selected House */}
      {selectedHouseRemedy && (
        <div className="bg-[#FAF6ED] border border-astro-border rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
          <div className="flex items-center gap-2.5">
            <span className="w-7 h-7 rounded-lg bg-astro-primary text-white flex items-center justify-center font-bold text-xs">
              {selectedHouseNumber}
            </span>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-[#263633] font-serif">
                  {isHi ? `भाव ${selectedHouseNumber} विशिष्ट कर्मिक संरेखण:` : `House ${selectedHouseNumber} Remedial Alignment:`}
                </span>
                <span className="text-[11px] font-bold text-astro-primary bg-white px-2 py-0.5 rounded border border-astro-border">
                  {isHi ? 'भावेश' : 'Lord'}: {getPlanetFullName(selectedHouseRemedy.houseLord, activeLang)}
                </span>
                {selectedHouseRemedy.dashaInfluenced && (
                  <span className="text-[10px] font-bold px-2 py-0.2 rounded-full bg-amber-100 text-amber-900 border border-amber-300">
                    ⚡ {isHi ? 'सक्रिय दशा से संबंधित' : 'Active Dasha Linked'}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-[#52635F] mt-0.5">
                {isHi ? selectedHouseRemedy.primaryKarmicFocus.hi : selectedHouseRemedy.primaryKarmicFocus.en}
              </p>
            </div>
          </div>

          <div className="text-[11px] bg-white p-2 rounded-lg border border-astro-border max-w-sm">
            <span className="font-bold text-astro-primary block mb-0.5 text-[10px] uppercase tracking-wider">
              {isHi ? 'शारीरिक एवं व्यावहारिक सुधार:' : 'Somatic & Behavioral Action:'}
            </span>
            <p className="text-[#263633] leading-tight text-[11px]">
              {isHi ? selectedHouseRemedy.behavioralAdjustment.hi : selectedHouseRemedy.behavioralAdjustment.en}
            </p>
          </div>
        </div>
      )}

      {/* VIEW 2: DYNAMIC UPAYA & KARMIC ACTION PLAN (Dasha, Gochar & Timing) */}
      {remedyViewMode === 'dynamic' && (
        <div className="space-y-4 animate-in fade-in duration-200">
          <div className="grid grid-cols-1 gap-3">
            {report.dynamicActionPlan.map((action, idx) => {
              const isUrgent = action.urgency === 'Immediate';
              return (
                <div
                  key={idx}
                  className={`rounded-2xl border p-4 sm:p-5 shadow-xs transition-all ${
                    isUrgent
                      ? 'bg-white border-amber-300/80 shadow-sm ring-1 ring-amber-200'
                      : 'bg-white border-astro-border hover:border-astro-primary/40'
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-astro-border/70 pb-3 mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-astro-primary text-white font-bold flex items-center justify-center text-xs shrink-0">
                        {getPlanetShortCode(action.planet, activeLang)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-bold text-astro-primary font-serif">
                            {isHi ? action.titleHi : action.titleEn}
                          </h4>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            isUrgent ? 'bg-amber-100 text-amber-900 border border-amber-300' : 'bg-astro-teal-tint text-astro-primary border border-astro-primary/20'
                          }`}>
                            {action.urgency}
                          </span>
                        </div>
                        <span className="text-[10px] text-astro-secondary-text font-mono">
                          {action.timeframe} • {action.impactZone}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 3-Pillar Practical Dynamic Remedy Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* 1. Core Karmic Guidance */}
                    <div className="bg-[#FAF6ED]/70 p-3 rounded-xl border border-astro-border/80 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-1.5 text-xs font-bold text-astro-primary mb-1.5">
                          <Compass className="w-3.5 h-3.5 text-astro-accent" />
                          <span>{isHi ? 'मुख्य कर्मिक कार्ययोजना' : 'Strategic Action Plan'}</span>
                        </div>
                        <p className="text-[11.5px] text-[#263633] leading-relaxed font-medium">
                          {isHi ? action.actionPlan.hi : action.actionPlan.en}
                        </p>
                      </div>
                    </div>

                    {/* 2. Somatic & Behavioral Routine (User's core request: exercise, meditation, sleep) */}
                    <div className="bg-[#FAF6ED]/70 p-3 rounded-xl border border-astro-border/80 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-800 mb-1.5">
                          <Activity className="w-3.5 h-3.5 text-emerald-600" />
                          <span>{isHi ? 'शारीरिक व व्यावहारिक क्रिया (Somatic)' : 'Somatic & Behavioral Routine'}</span>
                        </div>
                        <p className="text-[11.5px] text-[#263633] leading-relaxed font-medium">
                          {isHi ? action.somaticAction.hi : action.somaticAction.en}
                        </p>
                      </div>
                    </div>

                    {/* 3. Daan, Mantra & Stotram */}
                    <div className="bg-[#FAF6ED]/70 p-3 rounded-xl border border-astro-border/80 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-1.5 text-xs font-bold text-astro-primary mb-1.5">
                          <Shield className="w-3.5 h-3.5 text-astro-primary" />
                          <span>{isHi ? 'सात्विक दान एवं स्तोत्र' : 'Charity (Daan) & Stotram'}</span>
                        </div>
                        <p className="text-[11.5px] text-[#263633] leading-relaxed font-medium">
                          {isHi ? action.daanOrPuja.hi : action.daanOrPuja.en}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* VIEW 1: CUMULATIVE NATAL FOUNDATION UPAYA (All 9 Grahas Comprehensive) */}
      {remedyViewMode === 'cumulative' && (
        <div className="space-y-4 animate-in fade-in duration-200">
          {/* Filter Pills */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-astro-border pb-2.5">
            <span className="text-xs font-bold text-astro-primary font-serif">
              {isHi ? '९ नवग्रह संचयी जीवन-उपाय विवरण:' : '9 Navagraha Foundation Remedies Matrix:'}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {['all', 'High', 'Benefic', 'Malefic'].map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategoryFilter(cat)}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                    selectedCategoryFilter === cat
                      ? 'bg-astro-primary text-white shadow-2xs'
                      : 'bg-astro-card text-astro-secondary-text hover:text-astro-text border border-astro-border'
                  }`}
                >
                  {cat === 'all' ? (isHi ? 'सभी ग्रह' : 'All 9 Grahas') : cat}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {report.cumulativePlanetaryRemedies
              .filter(p => {
                if (selectedCategoryFilter === 'High') return p.priority === 'High';
                if (selectedCategoryFilter === 'Benefic') return p.isBenefic;
                if (selectedCategoryFilter === 'Malefic') return !p.isBenefic;
                return true;
              })
              .map((item, idx) => {
                const isExpanded = expandedPlanets[item.planet] ?? (item.priority === 'High');
                return (
                  <div
                    key={idx}
                    className="bg-white border border-astro-border rounded-2xl overflow-hidden shadow-xs hover:border-astro-primary/40 transition-all flex flex-col justify-between"
                  >
                    {/* Header Bar */}
                    <div
                      onClick={() => togglePlanet(item.planet)}
                      className="p-3.5 bg-[#FAF6ED]/60 hover:bg-[#FAF6ED] border-b border-astro-border/70 flex items-center justify-between cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-lg font-bold flex items-center justify-center text-xs text-white ${
                          item.isBenefic ? 'bg-emerald-700' : 'bg-astro-primary'
                        }`}>
                          {getPlanetShortCode(item.planet, activeLang)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-xs sm:text-sm text-astro-primary font-serif">
                              {isHi ? item.sanskritName : getPlanetFullName(item.planet, activeLang)}
                            </span>
                            <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${
                              item.priority === 'High' ? 'bg-amber-100 text-amber-900 border border-amber-300' : 'bg-astro-card text-astro-secondary-text border border-astro-border'
                            }`}>
                              {item.priority} {isHi ? 'प्राथमिकता' : 'Priority'}
                            </span>
                          </div>
                          <span className="text-[10px] text-astro-secondary-text">
                            {item.role}
                          </span>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="p-1 hover:bg-black/5 rounded-full text-astro-secondary-text transition-colors"
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>

                    {/* Compact Card Content */}
                    <div className="p-3.5 space-y-3 text-xs">
                      {/* Somatic & Behavioral Action Plan (Highlight) */}
                      <div className="bg-emerald-50/60 p-2.5 rounded-xl border border-emerald-200">
                        <span className="text-[10px] font-bold text-emerald-900 flex items-center gap-1 uppercase tracking-wider mb-1">
                          <Activity className="w-3 h-3 text-emerald-700" />
                          {isHi ? 'शारीरिक व व्यावहारिक दिनचर्या' : 'Somatic & Behavioral Practice'}
                        </span>
                        <p className="text-[11px] text-emerald-950 leading-relaxed font-medium">
                          {isHi ? item.somaticBehavioral.hi : item.somaticBehavioral.en}
                        </p>
                      </div>

                      {/* Detailed Elements (Expanded) */}
                      {isExpanded && (
                        <div className="space-y-2.5 pt-1 border-t border-astro-border/50 animate-in fade-in duration-150">
                          {/* Gemstone / Alternative */}
                          <div className="bg-[#FAF6ED] p-2.5 rounded-xl border border-astro-border flex flex-col gap-1">
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-astro-primary text-[11px] flex items-center gap-1">
                                <Gem className="w-3.5 h-3.5 text-astro-accent" /> {item.gemstoneAndAlternative.primaryGem}
                              </span>
                              <span className="text-[10px] font-mono text-astro-secondary-text">
                                {item.gemstoneAndAlternative.metal} • {item.gemstoneAndAlternative.wearingFinger}
                              </span>
                            </div>
                            <span className="text-[10px] text-astro-secondary-text italic">
                              {isHi ? 'वैकल्पिक उप-रत्न: ' : 'Substitute: '}{item.gemstoneAndAlternative.substituteGem}
                            </span>
                            <p className="text-[10px] text-astro-primary mt-0.5">
                              {isHi ? item.gemstoneAndAlternative.suitabilityNote.hi : item.gemstoneAndAlternative.suitabilityNote.en}
                            </p>
                          </div>

                          {/* Mantra Upasana */}
                          <div className="bg-white p-2.5 rounded-xl border border-astro-border space-y-1">
                            <span className="font-bold text-astro-primary text-[10.5px] block">
                              {isHi ? 'तांत्रिक बीज मंत्र:' : 'Tantric Beej Mantra:'}
                            </span>
                            <p className="font-mono text-xs text-astro-primary font-semibold bg-[#FAF6ED] p-1.5 rounded border border-astro-border/60">
                              {item.mantraUpasana.tantricBeejMantra}
                            </p>
                            <span className="text-[9.5px] text-astro-secondary-text block">
                              {isHi ? `जप संख्या: १०८ बार (${item.mantraUpasana.recommendedTime})` : `Daily Count: 108 times (${item.mantraUpasana.recommendedTime})`}
                            </span>
                          </div>

                          {/* Daan & Charity */}
                          <div className="bg-white p-2.5 rounded-xl border border-astro-border text-[11px] space-y-1">
                            <span className="font-bold text-astro-primary text-[10.5px]">
                              {isHi ? 'सात्विक दान सामग्री:' : 'Charity & Donation:'}
                            </span>
                            <p className="text-[#263633] text-[10.5px]">
                              {isHi ? item.daanAndCharity.itemsToDonate.hi : item.daanAndCharity.itemsToDonate.en}
                            </p>
                            <span className="text-[9.5px] text-astro-secondary-text block">
                              {isHi ? 'स्थान/पात्र: ' : 'Recipient: '}{isHi ? item.daanAndCharity.recipientOrLocation.hi : item.daanAndCharity.recipientOrLocation.en}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};
